import { Inject, Injectable } from '@angular/core';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, AuthenticationResult, IPublicClientApplication, EventType, EventMessage } from '@azure/msal-browser';
import { filter, map, Observable, switchMap, tap, from, firstValueFrom, catchError } from 'rxjs';
import { RuntimeConfigService } from '../config/runtime-config.service';
import { MSAL_B2C_INSTANCE } from '../../../msal-instances';
import { MsalInitializerService } from './msal-initializer.service';

@Injectable({ providedIn: 'root' })
export class B2CLoginService {
  private isInitializing = false;

  constructor(
    @Inject(MSAL_B2C_INSTANCE) private msalInstance: IPublicClientApplication,
    private broadcast: MsalBroadcastService,
    private runtimeConfig: RuntimeConfigService,
    private msalInitializer: MsalInitializerService
  ) {
    // Initialize the MSAL instance when the service is instantiated
    this.ensureInitialized()
      .then(() => {
        // Handle any unfinished login redirects
        this.msalInstance.handleRedirectPromise()
          .catch(err => {
            console.error('[B2CLoginService] Error during redirect handling in constructor:', err);
          });
      });
  }
  
  /**
   * Ensure the MSAL instance is initialized
   */
  private async ensureInitialized(): Promise<void> {
    if (this.isInitializing) {
      return;
    }
    
    this.isInitializing = true;
    try {
      await this.msalInitializer.initializeB2CInstance(this.msalInstance);
    } catch (error) {
      console.error('[B2CLoginService] Failed to initialize MSAL:', error);
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Initiates the Azure B2C login redirect flow
   */
  async loginRedirect(): Promise<void> {
    try {
      // Make sure MSAL is initialized before attempting to login
      await this.ensureInitialized();
      
      // Get B2C policy and tenant information from runtime config
      const b2cConfig = this.runtimeConfig.auth?.azureB2C;

      if (!b2cConfig) {
        console.error('[B2CLoginService] B2C configuration is not available');
        throw new Error('B2C configuration is not available');
      }

      // Configure the B2C authority URL with the correct policy (user flow)
      const authorityUrl = `https://${b2cConfig.tenant}.b2clogin.com/${b2cConfig.tenant}.onmicrosoft.com/${b2cConfig.policy}`;
      console.log('[B2CLoginService] Using authority URL:', authorityUrl);
      
      // Configure MSAL client to use B2C for this specific login
      const b2cRequest = {
        scopes: ['openid', 'profile', 'email'],
        authority: authorityUrl
      };

      // Initiate login redirect
      console.log('[B2CLoginService] Starting login redirect with request:', b2cRequest);
      await this.msalInstance.loginRedirect(b2cRequest);
    } catch (error) {
      console.error('[B2CLoginService] Login redirect failed:', error);
      throw error;
    }
  }

  /**
   * Initiates the Azure B2C login with username as login hint
   * This provides a smoother user experience without collecting passwords in the app
   * @param username The user's email to pass as login_hint to B2C
   */
  async loginWithHint(username: string): Promise<void> {
    try {
      // Make sure MSAL is initialized before attempting to login
      await this.ensureInitialized();

      // Get B2C policy and tenant information from runtime config
      const b2cConfig = this.runtimeConfig.auth?.azureB2C;
      console.log('[B2CLoginService] Using B2C config:', b2cConfig);
      console.log('[B2CLoginService] Login with hint for username:', username);

      if (!b2cConfig) {
        console.error('[B2CLoginService] B2C configuration is not available');
        throw new Error('B2C configuration is not available');
      }

      // Configure the B2C authority URL with the standard policy
      const authorityUrl = `https://${b2cConfig.tenant}.b2clogin.com/${b2cConfig.tenant}.onmicrosoft.com/${b2cConfig.policy}`;

      // Configure MSAL client to use B2C with login_hint parameter
      // This pre-populates the username field in the B2C interface
      const b2cRequest = {
        scopes: ['openid', 'profile', 'email'],
        authority: authorityUrl,
        loginHint: username
      };

      // Initiate login redirect with the hint
      console.log('[B2CLoginService] Starting login redirect with hint:', username);
      await this.msalInstance.loginRedirect(b2cRequest);
    } catch (error) {
      console.error('[B2CLoginService] Login with hint failed:', error);
      throw error;
    }
  }

  /**
   * Handles a redirect response from Azure B2C authentication
   * Returns an Observable that resolves when the redirect is complete
   * and the token has been acquired
   */
  onRedirectComplete$(): Observable<AuthenticationResult> {
    console.log('[B2CLoginService] Starting to handle redirect response...');
    
    // First ensure the MSAL instance is initialized
    return from(this.ensureInitialized()).pipe(
      tap(() => console.log('[B2CLoginService] MSAL initialized, handling redirect promise...')),
      // Then handle the redirect
      switchMap(() => {
        try {
          return from(this.msalInstance.handleRedirectPromise()).pipe(
            catchError(error => {
              console.error('[B2CLoginService] Error in handleRedirectPromise:', error);
              throw new Error(`MSAL handleRedirectPromise failed: ${error.message}`);
            })
          );
        } catch (error) {
          console.error('[B2CLoginService] Exception in handleRedirectPromise:', error);
          throw new Error(`Exception in handleRedirectPromise: ${error.message}`);
        }
      }),
      tap(response => console.log('[B2CLoginService] Redirect promise handled, response:', response ? 'exists' : 'null')),
      // Filter out null responses (happens when there's no redirect response to handle)
      filter(response => {
        const hasResponse = !!response;
        if (!hasResponse) {
          console.log('[B2CLoginService] No response from redirect, will try silent token acquisition');
        }
        return hasResponse;
      }),
      // Ensure the response is from B2C
      filter(response => {
        const isB2CResponse = response?.account?.idTokenClaims?.iss?.includes('b2clogin.com');
        if (!isB2CResponse) {
          console.log('[B2CLoginService] Response is not from B2C, ignoring');
        }
        return isB2CResponse;
      }),
      // If no response from handleRedirectPromise, try to get the token silently
      switchMap(response => {
        if (response) {
          console.log('[B2CLoginService] Using redirect response');
          return from(Promise.resolve(response));
        } else {
          console.log('[B2CLoginService] Trying to acquire token silently');
          // No response, so try to get the token silently from a B2C account
          return this.acquireTokenSilent$().pipe(
            catchError(error => {
              console.error('[B2CLoginService] Silent token acquisition failed:', error);
              throw new Error(`Silent token acquisition failed: ${error.message}`);
            })
          );
        }
      }),
      // Log the result
      tap(result => console.log('[B2CLoginService] Authentication result:', result)),
      catchError(error => {
        console.error('[B2CLoginService] Error handling redirect:', error);
        throw error;
      })
    );
  }

  /**
   * Subscribe to login success events from B2C
   */
  onLoginSuccess$(): Observable<EventMessage> {
    return this.broadcast.msalSubject$.pipe(
      filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
      filter((msg: EventMessage) => {
        // Check if this login is from B2C
        if (msg.payload && 'account' in msg.payload) {
          const account = msg.payload.account;
          return account?.idTokenClaims?.iss?.includes('b2clogin.com');
        }
        return false;
      })
    );
  }

  /**
   * Attempts to acquire a token silently for the current B2C user
   */
  acquireTokenSilent$(): Observable<AuthenticationResult> {
    return from(
      new Promise<AuthenticationResult>((resolve, reject) => {
        const accounts = this.getAllAccounts();
        
        if (accounts.length === 0) {
          reject(new Error('No B2C accounts found'));
          return;
        }
        
        // Use the first account
        const account = accounts[0];
        const b2cConfig = this.runtimeConfig.auth?.azureB2C;
        const authorityUrl = `https://${b2cConfig?.tenant}.b2clogin.com/${b2cConfig?.tenant}.onmicrosoft.com/${b2cConfig?.policy}`;
        
        this.msalInstance.acquireTokenSilent({
          account,
          scopes: ['openid', 'profile', 'email'],
          authority: authorityUrl
        })
        .then(result => resolve(result))
        .catch(error => reject(error));
      })
    );
  }
  
  /**
   * Get all accounts authenticated with B2C
   */
  getAllAccounts() {
    return this.msalInstance.getAllAccounts().filter(account => {
      // Only include B2C accounts
      return account.idTokenClaims?.iss?.includes('b2clogin.com');
    });
  }
  
  /**
   * Logout the user from B2C
   */
  logout() {
    // Get the B2C account
    const accounts = this.getAllAccounts();
    if (accounts.length > 0) {
      // Get B2C policy and tenant information from runtime config
      const b2cConfig = this.runtimeConfig.auth?.azureB2C;
      if (b2cConfig) {
        const authorityUrl = `https://${b2cConfig.tenant}.b2clogin.com/${b2cConfig.tenant}.onmicrosoft.com/${b2cConfig.policy}`;
        
        // Use logoutRedirect with the B2C authority
        return this.msalInstance.logoutRedirect({
          authority: authorityUrl
        });
      }
    }
    // Fall back to regular logout
    return this.msalInstance.logout();
  }
}
